import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, onSnapshot, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, limit, where } from 'firebase/firestore';
import { updatePassword } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import { Plus, Edit, Trash2, User, Mail, Calendar, Eye, EyeOff, X } from 'lucide-react';
import ConfirmationModal from '../components/ConfirmationModal';
import useConfirmation from '../hooks/useConfirmation';
import Portal from '../components/Portal';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const EMPTY_USER_FORM = {
  nome: '',
  email: '',
  password: '',
  confirmPassword: ''
};

const EMPTY_SHOW_PASSWORD = { password: false, confirmPassword: false };

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(() => ({ ...EMPTY_USER_FORM }));
  const [showPassword, setShowPassword] = useState({ ...EMPTY_SHOW_PASSWORD });
  const userModalSheetRef = useRef(null);
  const { confirmation, showConfirmation, hideConfirmation, confirmAction, cancelAction } = useConfirmation();
  const { user: currentUser } = useAuth();

  const closeUserModal = useCallback(() => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ ...EMPTY_USER_FORM });
    setShowPassword({ ...EMPTY_SHOW_PASSWORD });
  }, []);

  useEffect(() => {
    if (!showModal) return;
    const scrollY = window.scrollY;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;
    const prevBodyHeight = document.body.style.height;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      document.body.style.height = prevBodyHeight;
      window.scrollTo(0, scrollY);
    };
  }, [showModal]);

  // Listener em tempo real para usuários
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      setUsers([]);
      return;
    }

    setLoading(true);

    const usersQuery = query(
      collection(db, 'usuarios'),
      orderBy('criadoEm', 'desc'),
      limit(200) // Limite de 200 usuários
    );
    
    let isCreatingCurrentUser = false;
    
    const unsubscribe = onSnapshot(
      usersQuery,
      async (snapshot) => {
        try {
          const usersData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              criadoEm: data.criadoEm ? data.criadoEm.toDate() : new Date(),
              firstLoginAt: data.firstLoginAt ? data.firstLoginAt.toDate() : null
            };
          });
          
          // Verificar se o usuário atual está na lista
          const currentUserExists = usersData.find(u => u.uid === currentUser?.uid);
          
          // Se o usuário atual não existe na lista, criar um registro para ele (apenas uma vez)
          if (currentUser && !currentUserExists && !isCreatingCurrentUser) {
            isCreatingCurrentUser = true;
            try {
              await addDoc(collection(db, 'usuarios'), {
                uid: currentUser.uid,
                nome: currentUser.nomeExibicao || currentUser.nome || currentUser.email?.split('@')[0] || 'Usuário',
                email: currentUser.email,
                status: 'active',
                precisaPrimeiroLogin: false,
                criadoEm: new Date()
              });
              // O listener atualizará automaticamente após criar
            } catch (error) {
              // Erro ao adicionar usuário atual
              isCreatingCurrentUser = false;
            }
          } else {
            setUsers(usersData);
            setLoading(false);
            isCreatingCurrentUser = false;
          }
        } catch (error) {
          setLoading(false);
          isCreatingCurrentUser = false;
        }
      },
      (error) => {
        // Callback de erro do onSnapshot
        setLoading(false);
        isCreatingCurrentUser = false;
      }
    );

    // Cleanup: remover listener quando o componente desmontar
    return () => {
      unsubscribe();
    };
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      await showConfirmation({
        title: 'Erro',
        messidade: 'As senhas não coincidem',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    if (formData.password && formData.password.length < 6) {
      await showConfirmation({
        title: 'Erro',
        messidade: 'A senha deve ter pelo menos 6 caracteres',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    try {
      const userData = {
        nome: formData.nome.trim(),
        email: formData.email.trim().toLowerCase(),
        status: 'pending',
        precisaPrimeiroLogin: true,
        password: formData.password, // Senha temporária para primeiro login
        criadoEm: new Date()
      };

      if (editingUser) {
        // Editar usuário existente
        const updateData = {
          nome: userData.nome,
          email: userData.email,
          atualizadoEm: new Date()
        };

        // Só atualizar senha se foi fornecida
        if (formData.password) {
          try {
            // Atualizar senha no Firebase Auth
            const user = auth.currentUser;
            if (user && user.uid === currentUser?.uid) {
              await updatePassword(user, formData.password);
            }
          } catch (authError) {
            await showConfirmation({
              title: 'Erro',
              messidade: 'Erro ao atualizar senha: ' + authError.message,
              confirmText: 'OK',
              showCancel: false
            });
            return;
          }
          
          // Atualizar senha também no Firestore para usuários pendentes
          updateData.password = formData.password;
          updateData.precisaPrimeiroLogin = true;
        }

        await updateDoc(doc(db, 'usuarios', editingUser.id), updateData);
        
        await showConfirmation({
          title: 'Sucesso',
          message: '',
          confirmText: 'OK',
          showCancel: false
        });
      } else {
        // Verificar se o email já existe no Firestore antes de criar
        const usersQuery = query(
          collection(db, 'usuarios'),
          where('email', '==', userData.email)
        );
        const snapshot = await getDocs(usersQuery);
        
        if (!snapshot.empty) {
          await showConfirmation({
            title: 'Erro',
            message: `O email "${userData.email}" já está em uso. Por favor, use outro email.`,
            confirmText: 'OK',
            showCancel: false,
            type: 'warning'
          });
          return;
        }

        // Criar novo usuário
        await addDoc(collection(db, 'usuarios'), userData);

        await showConfirmation({
          title: 'Usuário criado',
          message: `Usuário criado com sucesso!\n\n`,
          confirmText: 'OK',
          showCancel: false,
          type: 'success'
        });
      }

      closeUserModal();

      // O listener em tempo real atualizará automaticamente
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        message: `Erro ao salvar usuário: ${error.message || 'Erro desconhecido'}`,
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    }
  };

  const handleEdit = async (user) => {
    // Verificar se está tentando editar outro usuário
    if (user.uid !== currentUser?.uid) {
      await showConfirmation({
        title: 'Acesso Negado',
        messidade: 'Você só pode editar suas próprias informações',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }
    
    setEditingUser(user);
    setFormData({
      nome: user.nome || '',
      email: user.email || '',
      password: '',
      confirmPassword: ''
    });
    setShowPassword({ ...EMPTY_SHOW_PASSWORD });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    const userToDelete = users.find(u => u.id === userId);
    
    // Verificar se está tentando excluir a si mesmo
    if (userToDelete?.uid === currentUser?.uid) {
      await showConfirmation({
        title: 'Acesso Negado',
        messidade: 'Você não pode excluir sua própria conta',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }
    
    const confirmed = await showConfirmation({
      title: 'Excluir Usuário',
      messidade: `Tem certeza que deseja excluir o usuário "${userToDelete?.nome}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'usuarios', userId));
      } catch (error) {
      await showConfirmation({
        title: 'Erro',
        messidade: 'Erro ao excluir usuário',
        confirmText: 'OK',
        showCancel: false
      });
    }
    
    // O listener em tempo real atualizará automaticamente
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground-light dark:text-foreground-dark flex items-center space-x-2">
            <User className="h-6 w-6" />
            <span>Usuários</span>
          </h1>
          <p className="hidden sm:block text-muted-light dark:text-muted-dark">Gerencie usuários do sistema</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Novo Usuário</span>
          <span className="sm:hidden">Novo</span>
        </button>
      </div>

      {/* Lista de Usuários — mobile: só os cards; md+: tabela dentro de card */}
      {users.length === 0 ? (
        <div className="rounded-lg border border-border-light bg-card-light shadow-sm dark:border-border-dark dark:bg-card-dark p-3 sm:p-6">
          <div className="text-center py-12">
            <User className="mx-auto mb-4 h-16 w-16 text-muted-light dark:text-muted-dark" />
            <h3 className="text-lg font-medium text-foreground-light dark:text-foreground-dark mb-2">Nenhum usuário cadastrado</h3>
            <p className="text-muted-light dark:text-muted-dark mb-4">Comece adicionando o primeiro usuário</p>
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary"
            >
              Adicionar Primeiro Usuário
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
              {users.map((user) => {
                const criadoStr = (() => {
                  const d = toDate(user.criadoEm);
                  return d ? format(d, 'dd/MM/yyyy', { locale: ptBR }) : '—';
                })();
                return (
                  <div
                    key={user.id}
                    className="rounded-lg border border-border-light bg-background-light p-3 dark:border-border-dark dark:bg-background-dark"
                  >
                    <div className="space-y-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <div className="shrink-0 rounded-lg bg-primary-100 p-1.5 dark:bg-primary-900/30">
                          <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-foreground-light dark:text-foreground-dark">
                            {user.nome || 'Nome não informado'}
                          </span>
                          {user.uid === currentUser?.uid && (
                            <span className="inline-flex shrink-0 items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                              Você
                            </span>
                          )}
                        </div>
                      </div>
                      {user.uid && (
                        <p className="break-all text-xs text-muted-light dark:text-muted-dark">
                          ID: {user.uid}
                        </p>
                      )}
                      <div className="flex items-start gap-2 text-sm text-foreground-light dark:text-foreground-dark">
                        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-muted-light dark:text-muted-dark" />
                        <span className="min-w-0 break-words">{user.email || '—'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground-light dark:text-foreground-dark">
                        <Calendar className="h-4 w-4 shrink-0 text-muted-light dark:text-muted-dark" />
                        <span>{criadoStr}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {user.uid === currentUser?.uid && (
                        <button
                          type="button"
                          onClick={() => handleEdit(user)}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border-light bg-card-light text-muted-light touch-manipulation transition-colors hover:text-primary-600 active:opacity-80 dark:border-border-dark dark:bg-card-dark dark:text-muted-dark"
                          title="Editar meu perfil"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {user.uid !== currentUser?.uid && (
                        <button
                          type="button"
                          onClick={() => handleDelete(user.id)}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border-light bg-card-light text-muted-light touch-manipulation transition-colors hover:text-danger-600 active:opacity-80 dark:border-border-dark dark:bg-card-dark dark:text-muted-dark"
                          title="Excluir usuário"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>

          <div className="hidden md:block rounded-lg border border-border-light bg-card-light shadow-sm dark:border-border-dark dark:bg-card-dark p-3 sm:p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border-light dark:divide-border-dark">
                <thead className="bg-secondary-light dark:bg-secondary-dark">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                      Criado em
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-light bg-card-light dark:divide-border-dark dark:bg-card-dark">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-secondary-light dark:hover:bg-secondary-dark">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="rounded-lg bg-primary-100 p-2">
                            <User className="h-5 w-5 text-primary-600" />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2 text-sm font-medium text-foreground-light dark:text-foreground-dark">
                              <span>{user.nome || 'Nome não informado'}</span>
                              {user.uid === currentUser?.uid && (
                                <span className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                  Você
                                </span>
                              )}
                            </div>
                            {user.uid && (
                              <div className="break-all text-xs text-muted-light dark:text-muted-dark">
                                ID: {user.uid}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-foreground-light dark:text-foreground-dark">
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-muted-light dark:text-muted-dark" />
                          <span>{user.email || '—'}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-foreground-light dark:text-foreground-dark">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-muted-light dark:text-muted-dark" />
                          <span>
                            {(() => {
                              const d = toDate(user.criadoEm);
                              return d ? format(d, 'dd/MM/yyyy', { locale: ptBR }) : '—';
                            })()}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-foreground-light dark:text-foreground-dark">
                        <div className="flex space-x-1">
                          {user.uid === currentUser?.uid && (
                            <button
                              type="button"
                              onClick={() => handleEdit(user)}
                              className="inline-flex items-center justify-center rounded-md p-1 text-muted-light transition-colors hover:text-primary-600 dark:text-muted-dark"
                              title="Editar meu perfil"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {user.uid !== currentUser?.uid && (
                            <button
                              type="button"
                              onClick={() => handleDelete(user.id)}
                              className="inline-flex items-center justify-center rounded-md p-1 text-muted-light transition-colors hover:text-danger-600 dark:text-muted-dark"
                              title="Excluir usuário"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal Novo/Editar Usuário (layout alinhado ao Pets / Agenda) */}
      {showModal && (
        <Portal>
          <div
            className="fixed inset-0 z-[99999] flex items-stretch justify-center bg-black bg-opacity-50 p-0 sm:items-center sm:p-4"
            data-modal="user"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeUserModal();
            }}
          >
            <div
              ref={userModalSheetRef}
              className="relative flex h-dvh max-h-dvh min-h-0 w-full flex-col overflow-hidden bg-card-light dark:bg-card-dark sm:h-auto sm:min-h-0 sm:max-h-[90vh] sm:max-w-2xl sm:rounded-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative z-50 flex shrink-0 items-center justify-between gap-2 border-b border-border-light bg-card-light px-4 py-4 dark:border-border-dark dark:bg-card-dark sm:px-6">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <User className="h-5 w-5 shrink-0 text-foreground-light dark:text-foreground-dark" />
                  <h2 className="min-w-0 text-xl font-bold text-foreground-light dark:text-foreground-dark">
                    {editingUser ? 'Editar Meu Perfil' : 'Novo Usuário'}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closeUserModal}
                  className="shrink-0 p-1 text-muted-light transition-colors hover:text-foreground-light dark:text-muted-dark dark:hover:text-foreground-dark"
                  aria-label="Fechar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form
                onSubmit={handleSubmit}
                className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6"
                style={{ overscrollBehavior: 'contain' }}
                noValidate
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <span
                      id="users-modal-nome-caption"
                      className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark"
                    >
                      Nome completo
                    </span>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      aria-labelledby="users-modal-nome-caption"
                      className="input-field !py-1.5"
                      placeholder="Ex: João Silva"
                      required
                    />
                  </div>

                  <div>
                    <span
                      id="users-modal-email-caption"
                      className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark"
                    >
                      Email
                    </span>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      aria-labelledby="users-modal-email-caption"
                      className="input-field !py-1.5"
                      placeholder="Ex: joao@exemplo.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <span
                      id="users-modal-password-caption"
                      className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark"
                    >
                      {editingUser ? 'Nova senha (deixe em branco para manter)' : 'Senha'}
                    </span>
                    <div className="relative">
                      <input
                        type={showPassword.password ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        aria-labelledby="users-modal-password-caption"
                        className="input-field !py-1.5 pr-10"
                        placeholder="Mínimo 6 caracteres"
                        required={!editingUser}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword({ ...showPassword, password: !showPassword.password })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-light dark:text-muted-dark hover:text-foreground-light dark:hover:text-foreground-dark"
                        aria-label={showPassword.password ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showPassword.password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <span
                      id="users-modal-confirm-password-caption"
                      className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark"
                    >
                      Confirmar senha
                    </span>
                    <div className="relative">
                      <input
                        type={showPassword.confirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        aria-labelledby="users-modal-confirm-password-caption"
                        className="input-field !py-1.5 pr-10"
                        placeholder="Digite a senha novamente"
                        required={!editingUser}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword({ ...showPassword, confirmPassword: !showPassword.confirmPassword })
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-light dark:text-muted-dark hover:text-foreground-light dark:hover:text-foreground-dark"
                        aria-label={showPassword.confirmPassword ? 'Ocultar confirmação' : 'Mostrar confirmação'}
                      >
                        {showPassword.confirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={closeUserModal} className="btn-secondary flex-1">
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    {editingUser ? 'Salvar alterações' : 'Criar usuário'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Portal>
      )}

      {/* Modal de Confirmação */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        onClose={hideConfirmation}
        onConfirm={confirmAction}
        onCancel={cancelAction}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
      />
    </div>
  );
};

export default Users;
